import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { PlusCircleIcon, CalendarIcon, PencilAltIcon, PencilIcon, CheckIcon } from "@heroicons/react/outline";
import { SignatureField, SignatureConfig } from "../interfaces";
import { WorkflowContext, useModal } from "../contexts";
import WorkflowSignaturesConfigModal from "./WorkflowSignaturesConfigModal";;

interface Props {
  recipientNumber: number;
  signatureConfig: SignatureConfig
  insertSignatureField: (field: SignatureField, recipient: number) => void;
}

function WorkflowSignatureFieldsOverlay(props: Props) {
  const { recipientNumber, signatureConfig, insertSignatureField } = props;
  const { t } = useTranslation();

  const workflowContext = useContext(WorkflowContext);
  const { workflow } = workflowContext;

  const { openModal } = useModal();

  const [clickedField, setClickedField] = useState<SignatureField | "TEXT_REQUIRED" | "CHECKBOX_REQUIRED">();

  async function handleInsertSignatureField(field: SignatureField, recipient: number) {
    setClickedField(field);
    setTimeout(() => setClickedField(undefined), 500);

    return insertSignatureField(field, recipient);
  }

  return (
    <div className={`flex flex-col`}>
      <div className="flex items-center text-sm border-b px-2 py-1">
        <PencilAltIcon className="w-4 h-4 mr-2" />
        <div className="font-semibold whitespace-nowrap py-2">
          {t("Signature")}
        </div>
        <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "SIGNATURE" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleInsertSignatureField("SIGNATURE", recipientNumber)}>
          {clickedField === "SIGNATURE" ? t("Inserting...") : <><PlusCircleIcon className="w-4 h-4 mr-1 flex-shrink-0" /> {t("Insert")}</>}
        </button>
      </div>

      <div className="flex items-center text-sm border-b px-2 py-1">
        <div className="w-4 h-4 mr-2 font-gilroy text-sm underline">
          AB
        </div>
        <div className="font-semibold whitespace-nowrap py-2">
          {t("Initials")}
        </div>
        <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "INITIALS" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleInsertSignatureField("INITIALS", recipientNumber)}>
          {clickedField === "INITIALS" ? t("Inserting...") : <><PlusCircleIcon className="w-4 h-4 mr-1 flex-shrink-0" /> {t("Insert")}</>}
        </button>
      </div>

      <div className="flex items-center text-sm border-b px-2 py-1">
        <div className="w-4 h-4 mr-2 font-gilroy text-sm underline">
          Tt
        </div>
        <div className="flex items-center gap-2 font-semibold whitespace-nowrap py-2">
          {t("Text")}
        </div>
        <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "TEXT" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleInsertSignatureField("TEXT", recipientNumber)}>
          {clickedField === "TEXT" ? t("Inserting...") : <><PlusCircleIcon className="w-4 h-4 mr-1 flex-shrink-0" /> {t("Insert")}</>}
        </button>

      </div>

      <div className="flex items-center text-sm border-b px-2 py-1">
        <div className="w-4 h-4 mr-2 font-gilroy text-sm border border-gray-600 rounded-sm text-center">
          Tt
        </div>
        <div className="flex items-center gap-2 font-semibold whitespace-nowrap py-2">
          {t("Text Area")}
        </div>

        <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "TEXTAREA" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleInsertSignatureField("TEXTAREA", recipientNumber)}>
          {clickedField === "TEXTAREA" ? t("Inserting...") : <><PlusCircleIcon className="w-4 h-4 mr-1 flex-shrink-0" /> {t("Insert")}</>}
        </button>
      </div>

      <div className="flex items-center text-sm border-b px-2 py-1 last:border-b-0">
        <CheckIcon className="w-4 h-4 mr-2 border border-black rounded" />
        <div className="flex items-center gap-2 font-semibold whitespace-nowrap py-2">
          {t("Checkbox")}
        </div>
        <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "CHECKBOX" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleInsertSignatureField("CHECKBOX", recipientNumber)}>
          {clickedField === "CHECKBOX" ? t("Inserting...") : <><PlusCircleIcon className="w-4 h-4 mr-1 flex-shrink-0" /> {t("Insert")}</>}
        </button>
      </div>

      <div className="flex items-center text-sm border-b px-2 py-1">
        <div className="flex flex-col">
          <div className="flex items-center">
            <CalendarIcon className="w-4 h-4 mr-2" />
            <div className="font-semibold whitespace-nowrap py-2">
              {t("Date")}
            </div>
            <div className="text-xs ml-auto flex items-start group cursor-pointer hover:text-blue hover:underline" onClick={() => openModal(<WorkflowSignaturesConfigModal {...workflowContext} />)}>
              {workflow.signatureConfig!.dateFormat || workflow.owner?.dateFormat}
              <div className="ml-1">
                {(signatureConfig.dateTimeFormat || workflow.owner?.timeFormat) === "HOUR_12" && "12Hr"}
                {(signatureConfig.dateTimeFormat || workflow.owner?.timeFormat) === "HOUR_24" && "24Hr"}
              </div>
              <PencilIcon className="opacity-0 group-hover:opacity-100 w-3 h-3 ml-1" />
            </div>
            <button className={`ml-auto btn btn-white px-2 py-1 w-20 ${clickedField === "DATE" ? "bg-blue text-white" : "bg-white text-blue"}`} onClick={() => handleInsertSignatureField("DATE", recipientNumber)}>
              {clickedField === "DATE" ? t("Inserting...") : <><PlusCircleIcon className="w-4 h-4 mr-1 flex-shrink-0" /> {t("Insert")}</>}
            </button>

          </div>
          <div className="text-xs mt-1">{t("Automatically populated with the current date of when the recipient completes the document.")}</div>
        </div>
      </div>
    </div>
  );
}

export default WorkflowSignatureFieldsOverlay;
